import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiSearch } from 'react-icons/fi';
import { subCategoriesAPI } from '../../services/api';

const SubCategoryForm = ({ subCategory, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  // Searchable category dropdown state
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    if (subCategory) {
      setFormData({
        category_id: subCategory.category_id || '',
        name: subCategory.name || '',
        description: subCategory.description || '',
        status: subCategory.status || 'active'
      });
      const selectedCategory = categories.find(cat => cat.id === subCategory.category_id);
      setCategorySearch(selectedCategory?.name || '');
    }
  }, [subCategory, categories]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleCategorySelect = (category) => {
    setFormData(prev => ({ ...prev, category_id: category.id }));
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
  };

  const handleClearCategory = () => {
    setFormData(prev => ({ ...prev, category_id: '' }));
    setCategorySearch('');
    setShowCategoryDropdown(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Sub-category name is required');
      return;
    }

    try {
      setLoading(true);
      if (subCategory) {
        await subCategoriesAPI.update(subCategory.id, formData);
        toast.success('Sub-category updated successfully');
      } else {
        await subCategoriesAPI.create(formData);
        toast.success('Sub-category created successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving sub-category:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {subCategory ? 'Edit Sub-Category' : 'Add Sub-Category'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                {formData.category_id ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-4 py-2 border border-green-300 bg-green-50 rounded-lg text-sm truncate">
                      {categorySearch}
                    </div>
                    <button
                      type="button"
                      onClick={handleClearCategory}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0"
                      title="Change category"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                        setShowCategoryDropdown(true);
                      }}
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder="Search category..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={!formData.category_id}
                    />
                  </div>
                )}
                {showCategoryDropdown && !formData.category_id && (
                  <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCategories.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">No categories found</div>
                    ) : (
                      filteredCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleCategorySelect(cat)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          {cat.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub-Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter sub-category name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description (optional)"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : subCategory ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubCategoryForm;
